import React from 'react';
import ReactMarkdown from "react-markdown";
import { useState } from 'react';
// import { BotMessageSquare } from "lucide-react";
import { Copy } from "lucide-react";
import { SquareCheckBig, ThumbsUp, ThumbsDown, Paperclip } from "lucide-react";
import ReadAloud from "./ReadAloud";
import OGFleetIcon from '../../../../assets/OGFLEET Logo(1).png';
import toast from 'react-hot-toast';
import { storeMessageFeedback } from '../../../api';

const CopyButton = ({ text, loading, isDarkTheme }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 5000); // Reset after 2 seconds
    });
  };

  return (
    <button
      onClick={handleCopy}
      className={`relative mt-2 transition-colors ${isDarkTheme ? "text-gray-400 hover:text-amber-500" : "text-gray-500 hover:text-amber-500"
        }`}
      title={copied ? 'Copied!' : 'Copy to clipboard'}
    >
      {copied ? (
        <SquareCheckBig className="w-4 h-4" />
      ) : (
        <Copy className="w-4 h-4" />
      )}
    </button>
  );
};

const FeedbackButtons = ({ messageId, onLike, onDislike, isDarkTheme }) => {
  const [liked, setLiked] = useState(false);
  const [disliked, setDisliked] = useState(false);

  const handleLike = async () => {
    if (!liked) {
      try {
        await storeMessageFeedback(messageId, 'positive');
        setLiked(true);
        setDisliked(false);
        toast.success("Thank you for your feedback!", {
          duration: 3000,
        });
        onLike(true);
      } catch (err) {
        toast.error("Failed to send feedback");
      }
    }
  };

  const handleDislike = async () => {
    if (!disliked) {
      try {
        await storeMessageFeedback(messageId, 'negative');
        setDisliked(true);
        setLiked(false);
        toast.success("Thank you for your feedback!", {
          duration: 3000,
        });
        onDislike(true);
      } catch (err) {
        toast.error("Failed to send feedback");
      }
    }
  };

  return (
    <div className="flex items-center gap-3 mt-2">
      <button
        onClick={handleLike}
        className={`transition-colors ${isDarkTheme ? "text-gray-400 hover:text-amber-500" : "text-gray-500 hover:text-amber-500"
          } ${liked ? '!text-amber-500' : ''}`}
        title="Like"
      >
        <ThumbsUp className="w-4 h-4" />
      </button>
      <button
        onClick={handleDislike}
        className={`transition-colors ${isDarkTheme ? "text-gray-400 hover:text-amber-500" : "text-gray-500 hover:text-amber-500"
          } ${disliked ? '!text-amber-500' : ''}`}
        title="Dislike"
      >
        <ThumbsDown className="w-4 h-4" />
      </button>
    </div>
  );
};

const Message = ({ messageId, content, sender, loading, isDarkTheme }) => {
  const [liked, setLiked] = useState(false);
  const [disliked, setDisliked] = useState(false);

  const handleLike = (isLiked) => {
    console.log(isLiked ? 'Liked:' : 'Unliked:', content);
    setLiked(isLiked);
    setDisliked(false);
    // Add your like logic here
  };

  const handleDislike = (isDisliked) => {
    console.log(isDisliked ? 'Disliked:' : 'Undisliked:', content);
    setDisliked(isDisliked);
    setLiked(false);
    // Add your dislike logic here
  };

  if (sender === 'file') {
    // Extract filename from the content
    const contentParts = content.split("Extracted Content");
    const filename = contentParts[0].trim() || "Unknown File";
    const fileExtension = filename.split('.').pop().toLowerCase() || '';

    // Get file type label based on extension
    const getFileType = (ext) => {
      const types = {
        pdf: 'PDF',
        doc: 'DOC',
        docx: 'DOCX',
        xls: 'XLS',
        xlsx: 'XLSX',
        txt: 'TXT',
        jpg: 'JPG',
        jpeg: 'JPEG',
        png: 'PNG',
        // Add more file types as needed
      };
      return types[ext] || '';
    };

    const fileType = getFileType(fileExtension);

    return (
      <div className={`${isDarkTheme
        ? 'bg-gray-800 border-gray-700 text-gray-200 shadow-lg shadow-gray-900/30'
        : 'bg-white border-gray-200 text-gray-800 shadow-md shadow-gray-200/50'} 
        py-2 px-4 rounded-lg border self-end max-w-md mt-2 mb-1 transition-all`}>
        <div className="flex items-center">
          <div className="flex-shrink-0 mr-3">
            <div className={`${isDarkTheme
              ? 'bg-gray-700'
              : 'bg-gray-100'} 
              p-2 rounded-md flex items-center justify-center`}>
              <Paperclip className={`w-5 h-5 ${isDarkTheme ? 'text-amber-400' : 'text-amber-500'}`} />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium truncate">{filename}</p>
            {fileType && (
              <p className={`text-xs ${isDarkTheme ? 'text-amber-400/80' : 'text-amber-600/80'} mt-1`}>
                {fileType}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
    style={{ 
      wordBreak: "break-word", 
      overflowWrap: "break-word" 
    }}
    className={`message rounded-lg break-words whitespace-pre-line flex ${sender === 'user'
      ? isDarkTheme
        ? 'bg-gray-700 border-l-4 border-amber-400/80 text-gray-300 self-end max-w-[75%] m-2 p-3 shadow-sm'
        : 'bg-amber-50 border-l-4 border-amber-400 text-gray-800 self-end max-w-[75%] m-2 p-3 shadow-sm'
      : isDarkTheme
        ? 'text-gray-300 self-start max-w-[100%] m-2 p-3 shadow-sm'
        : 'bg-white text-gray-800 self-start max-w-[100%] m-2 p-3 shadow-sm'
      }`}>

      {sender !== 'user' && (
        <div className="flex-shrink-0 mr-2">
          <div>
            <img
              src={OGFleetIcon}
              alt="OGFLEET Icon"
              className={`w-[40px] h-[33px] -mt-1`}
            />
          </div>
        </div>
      )}


      <div className="flex-1">
        <ReactMarkdown
          components={{
            pre: ({ node, ...props }) => (
              <pre
                className={`whitespace-pre-wrap break-words overflow-x-auto p-3 rounded-md max-w-full border-l-4 border-amber-400/80 my-2 ${isDarkTheme
                  ? 'bg-gray-700 text-gray-300'
                  : 'bg-gray-100 text-gray-800'
                  }`}
                {...props}
              />
            ),
            code: ({ node, inline, ...props }) => (
              <code
                className={`${inline
                  ? 'px-1 py-0.5 rounded-md'
                  : 'p-2 rounded-md'
                  } ${isDarkTheme
                    ? 'bg-gray-700 text-gray-300'
                    : 'bg-gray-100 text-gray-800'
                  }`}
                {...props}
              />
            ),
            a: ({ node, ...props }) => (
              <a
                className={`${isDarkTheme
                  ? 'text-amber-400 hover:text-amber-300'
                  : 'text-amber-600 hover:text-amber-800'
                  } underline`}
                {...props}
              />
            ),
            h1: ({ node, ...props }) => (
              <h1
                className={`text-2xl font-bold ${isDarkTheme ? 'text-gray-300' : 'text-gray-900'}  my-4`}
                {...props}
              />
            ),
            h2: ({ node, ...props }) => (
              <h2
                className={`text-xl font-bold ${isDarkTheme ? 'text-gray-300' : 'text-gray-900'}  my-3`}
                {...props}
              />
            ),
            h3: ({ node, ...props }) => (
              <h3
                className={`text-lg font-bold ${isDarkTheme ? 'text-gray-300' : 'text-gray-900'} my-2`}
                {...props}
              />
            ),
            ul: ({ node, ...props }) => (
              <ul
                className="list-disc pl-5 my-2 space-y-1"
                {...props}
              />
            ),
            ol: ({ node, ...props }) => (
              <ol
                className="list-decimal pl-5 my-2 space-y-1"
                {...props}
              />
            ),
          }}
        >
          {content}
        </ReactMarkdown>
        {sender !== 'user' && (
          <div className="flex items-center gap-3 mt-2">
            <CopyButton text={content} loading={loading} />
            <FeedbackButtons messageId={messageId} onLike={handleLike} onDislike={handleDislike} />
            <ReadAloud text={content} isDarkTheme={isDarkTheme} />
          </div>
        )}
      </div>
    </div>
  );
};

export default Message;